import { DeleteOutline } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import {
    ReactNode,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { fetchOrder } from 'entities/Order';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrderId,
} from 'entities/Order/model/selectors/ordersSelectors';
import { useNavigationBlock } from 'features/NavigationBlockModal/ui/NavigationBlockProvider/NavigationBlockProvider';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';
import { getRouteNotFound, getRouteOrdersView } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { Loader } from 'shared/ui/Loader/Loader';
import RemoveOrderModal from 'shared/ui/RemoveOrderModal/RemoveOrderModal';

type RemoveLoadingSource = 'button' | 'external';

export interface RemoveItemFromOrderRenderButtonParams {
    onClick: () => void;
    isLoading: boolean;
    isDisabled: boolean;
}

interface RemoveItemFromOrderProps {
    itemId: string;
    showConfirmationDialog?: boolean;
    renderButton?: (params: RemoveItemFromOrderRenderButtonParams) => ReactNode;
    onRemoveLoadingChange?: (source: RemoveLoadingSource | null) => void;
}

/**
 * Ref interface for programmatically triggering the delete dialog
 */
export interface RemoveItemFromOrderRef {
    triggerDelete: () => void;
}

/**
 * Delete item component with confirmation dialog.
 * Supports custom button via render props and ref-based triggering.
 */
export const RemoveItemFromOrder = forwardRef<
    RemoveItemFromOrderRef,
    RemoveItemFromOrderProps
>(
    (
        {
            itemId,
            renderButton,
            showConfirmationDialog = true,
            onRemoveLoadingChange,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const navigate = useNavigate();
        const location = useLocation();
        const { validateNavBlock } = useNavigationBlock();
        const selectedOrderId = useSelector(getSelectedOrderId);
        const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);

        const [showDialog, setShowDialog] = useState(false);
        const [loadingSource, setLoadingSource] =
            useState<RemoveLoadingSource | null>(null);
        const submitInFlightRef = useRef(false);
        const deleteSourceRef = useRef<RemoveLoadingSource>('button');

        const isSubmitting = loadingSource !== null;

        useEffect(() => {
            onRemoveLoadingChange?.(loadingSource);
        }, [loadingSource, onRemoveLoadingChange]);

        const closeDialog = useCallback(() => {
            setShowDialog(false);
        }, []);

        const redirectOnNotFound = useCallback(
            () => navigate(getRouteNotFound()),
            [navigate],
        );

        const submitDialog = useCallback(async () => {
            if (submitInFlightRef.current) {
                return;
            }
            submitInFlightRef.current = true;
            const source = deleteSourceRef.current;
            setLoadingSource(source);
            try {
                const params = {
                    orderId: selectedOrderId,
                    itemId: itemId,
                    qty: 0,
                };
                const deleteResult = await dispatch(deleteOrderItem(params));
                if (deleteOrderItem.rejected.match(deleteResult)) {
                    return;
                }
                const fetchResult = await dispatch(
                    fetchOrder({
                        id: selectedOrderId,
                        onNotFound: redirectOnNotFound,
                    }),
                );
                if (fetchOrder.rejected.match(fetchResult)) {
                    return;
                }

                const targetRoute = getRouteOrdersView(selectedOrderId);
                const isSameRoute = location.pathname === targetRoute;

                if (isSameRoute) {
                    dispatch(catalogItemActions.removeItem(itemId));
                    setShowDialog(false);
                } else {
                    validateNavBlock(() => {
                        navigate(targetRoute);
                        dispatch(catalogItemActions.removeItem(itemId));
                        setShowDialog(false);
                    }, false);
                }
            } finally {
                submitInFlightRef.current = false;
                setLoadingSource(null);
            }
        }, [
            dispatch,
            itemId,
            location.pathname,
            navigate,
            redirectOnNotFound,
            selectedOrderId,
            validateNavBlock,
        ]);

        const openDialog = useCallback(
            (source: RemoveLoadingSource) => {
                if (isOrderSubmissionLocked || isSubmitting) {
                    return;
                }

                deleteSourceRef.current = source;

                if (showConfirmationDialog) {
                    setShowDialog(true);
                } else {
                    submitDialog();
                }
            },
            [
                isOrderSubmissionLocked,
                isSubmitting,
                showConfirmationDialog,
                submitDialog,
            ],
        );

        const openDialogFromButton = useCallback(() => {
            openDialog('button');
        }, [openDialog]);

        const openDialogFromExternal = useCallback(() => {
            openDialog('external');
        }, [openDialog]);

        useImperativeHandle(
            ref,
            () => ({
                triggerDelete: openDialogFromExternal,
            }),
            [openDialogFromExternal],
        );

        const isButtonLoading = loadingSource === 'button';

        return (
            <Box>
                {renderButton ? (
                    renderButton({
                        onClick: openDialogFromButton,
                        isLoading: isButtonLoading,
                        isDisabled: isSubmitting,
                    })
                ) : (
                    <Button
                        disabled={isOrderSubmissionLocked || isSubmitting}
                        aria-busy={isButtonLoading}
                        sx={{
                            padding: '6px',
                            minWidth: '25px',
                            '& .MuiButton-startIcon': {
                                margin: 0,
                                color: 'rgba(0, 0, 0, 0.30)',
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                '& .MuiButton-startIcon': {
                                    color: 'rgba(0, 0, 0, 0.50)',
                                },
                            },
                        }}
                        startIcon={
                            isButtonLoading ? (
                                <Loader size={20} color="inherit" />
                            ) : (
                                <DeleteOutline
                                    sx={{
                                        fontSize: '2.5rem !important',
                                    }}
                                />
                            )
                        }
                        onClick={openDialogFromButton}
                        data-testid="btn-order-item-delete"
                    />
                )}
                <RemoveOrderModal
                    title={t('Delete Item from the Order')}
                    text={t('You are about to remove this item from an order.')}
                    deleteButtonText={`Delete item`}
                    isShowDeleteOrder={showDialog}
                    isLoadingDeleteOrder={isSubmitting}
                    onCloseDeleteOrder={closeDialog}
                    onSubmitDeleteOrder={submitDialog}
                />
            </Box>
        );
    },
);

RemoveItemFromOrder.displayName = 'RemoveItemFromOrder';
